import { Array2D, ClientData, ID, Map2D, MapAction, MapND, mod, SubjectData, useJeffect, useLogger } from "@kosmod/kit";
import { createEmptySpace2D, createSpace2DFromChunkMap, Space2DInterface } from "../../lib/space"
import { TerrainBrush, TerrainBrushID, TerrainCanvas, TerrainCanvasID, TerrainChunk } from "../../defs";
import { Observable, Subject } from "rxjs";
import { SceneSessionClient } from "../../backend";
import { useEffect, useMemo, useRef, useState } from "@lukekaalim/act";
import { getChunkDataIndex, integerCoordinates } from "../../lib/chunks";
import * as three from 'three';
import { s, Shape } from "@kosmod/pancake";
import { CanvasCoordinator, ChunkKey, ChunkMap, createChunkMap, ReadonlyChunkMap, RichChunk, Set2D } from "@kosmod/stew";

type QuadTuple<T> = [T, T, T, T];

/**
 * Precomputed terrain adjacency information for Chunk Rendering
 * Clockwise (starting from north)
 */
export type TerrainSystemData = {
  floor: null | TerrainBrushID,
  walls: QuadTuple<null | TerrainBrushID>,
  neighborFloors: QuadTuple<null | TerrainBrushID>,
  neighborWalls: QuadTuple<[left: null | TerrainBrushID, right: null | TerrainBrushID]>,
};

export type TerrainSpace2D = Space2DInterface<null | TerrainSystemData>;

/**
 * Return a list of chunks
 * @param chunks
 * @returns 
 */
const projectAxially = (values: [number, number][]) => {
  return [...new Set2D(values.flatMap(([x, y]) => [
    [x + 1, y],
    [x - 1, y],
    [x, y + 1],
    [x, y - 1],
    [x, y],
  ]))];
}

export const updateTerrainSystemDataChunk = (
  source: ReadonlyChunkMap<number>,
  canvas: TerrainCanvas,
  chunk: RichChunk<TerrainSystemData | null>,
) => {
  // const chunk = cache.gen(chunkX, chunkY);

  for (const [localX, localY] of chunk.data.keys()) {
    const x = localX + (chunk.x * 8);
    const y = localY + (chunk.y * 8);

    let [floor, topWall, leftWall]  = Shape.Binary.unpackBytes(source.read(x, y));

    let [topFloor, , neighborTopLeftWall] =         Shape.Binary.unpackBytes(source.read(x, y - 1));
    let [, , neighborTopRightWall] =                Shape.Binary.unpackBytes(source.read(x + 1, y - 1));

    let [rightFloor, neighborRightTopWall, rightWall]   =         Shape.Binary.unpackBytes(source.read(x + 1, y));
    let [, neighborRightBottomWall, neighborBottomRightWall]   =  Shape.Binary.unpackBytes(source.read(x + 1, y + 1));

    let [bottomFloor, bottomWall, neighborBottomLeftWall]  = Shape.Binary.unpackBytes(source.read(x, y + 1));

    let [leftFloor, neighborLeftTopWall,]              = Shape.Binary.unpackBytes(source.read(x - 1, y));
    let [, neighborLeftBottomWall,]              = Shape.Binary.unpackBytes(source.read(x - 1, y + 1));
    

    const hasWall = topWall || leftWall || rightWall || bottomWall;
    const hasFloor = floor || topFloor || rightFloor || bottomFloor || leftFloor;

    if (hasWall || hasFloor) {
      let output = chunk.data.get(localX, localY);
      if (!output) {
        output = {
          floor: null,

          walls: [null, null, null, null],
          neighborFloors: [null, null, null, null],
          neighborWalls: [[null, null], [null, null], [null, null], [null, null]],
        }
        chunk.data.set(localX, localY, output);
      }

      output.floor = canvas.palette[floor - 1]                    || null

      output.walls[0] = canvas.palette[topWall - 1]               || null;
      output.walls[1] = canvas.palette[rightWall - 1]             || null;
      output.walls[2] = canvas.palette[bottomWall - 1]            || null;
      output.walls[3] = canvas.palette[leftWall - 1]              || null;

      output.neighborFloors[0] = canvas.palette[topFloor - 1]     || null;
      output.neighborFloors[1] = canvas.palette[rightFloor - 1]   || null;
      output.neighborFloors[2] = canvas.palette[bottomFloor - 1]  || null;
      output.neighborFloors[3] = canvas.palette[leftFloor - 1]    || null;

      output.neighborWalls[0][0] = canvas.palette[neighborTopLeftWall - 1]      || null;
      output.neighborWalls[0][1] = canvas.palette[neighborTopRightWall - 1]     || null;
      
      output.neighborWalls[1][0] = canvas.palette[neighborRightTopWall - 1]     || null;
      output.neighborWalls[1][1] = canvas.palette[neighborRightBottomWall - 1]  || null;

      output.neighborWalls[2][0] = canvas.palette[neighborBottomRightWall - 1]  || null;
      output.neighborWalls[2][1] = canvas.palette[neighborBottomLeftWall - 1]   || null;

      output.neighborWalls[3][0] = canvas.palette[neighborLeftBottomWall - 1]   || null;
      output.neighborWalls[3][1] = canvas.palette[neighborLeftTopWall - 1]      || null;
    } else {
      chunk.data.set(localX, localY, null)
    }
  }
}

export const useChunkMapKeys = (chunkMap: ReadonlyChunkMap<unknown>, coordinator: CanvasCoordinator) => {
  const [keys, setKeys] = useState<[x: number, y: number][]>([...chunkMap.chunks.keys()])

  useJeffect(j => {
    setKeys([...chunkMap.chunks.keys()]);

    j.add(coordinator.updates.subscribe(actions => {

      const updatedKeys = actions
        .filter(([key]) => key[0] === chunkMap.canvasId)
        .map(([key]) => [key[1], key[2]] as [number, number]);

      setKeys(keys => [...new Set2D([...keys, ...updatedKeys])])
    }));
  }, [chunkMap, coordinator]);

  return keys;
}

export type WalkableNode = {
  U: boolean,
  D: boolean,
  L: boolean,
  R: boolean,
}
export const WalkableNode = {
  EMPTY() {
    return { U: false, D: false, L: false, R: false };
  }
}

export const updateWalkableChunk = (
  source: ReadonlyChunkMap<TerrainSystemData | null>,
  // canvas: TerrainCanvas,
  // brushes: TerrainBrush[],
  chunk: RichChunk<WalkableNode | null>,
) => {
  // logger.log('Gen Chunk', chunkX, chunkY)
  // const chunk = walkableChunks.gen(chunkX, chunkY);

  for (const [localX, localY] of chunk.data.keys()) {
    const x = localX + (chunk.x * 8);
    const y = localY + (chunk.y * 8);

    const data = source.read(x, y);
    const isWalkable = data && data.floor !== null;

    if (!data || !isWalkable) {
      chunk.data.set(localX, localY, null)
      continue;
    }

    const isRightWalkable = data.walls[1] === null && data.neighborFloors[1] !== null;
    const isBottomWalkable = data.walls[2] === null && data.neighborFloors[2] !== null;
    const isTopWalkable = data.walls[0] === null && data.neighborFloors[0] !== null;
    const isLeftWalkable = data.walls[3] === null && data.neighborFloors[3] !== null;

    let output = chunk.data.get(localX, localY);
    if (!output) {
      output = WalkableNode.EMPTY();
      chunk.data.set(localX, localY, output);
    }
    output.R = isRightWalkable;
    output.D = isBottomWalkable;
    output.L = isLeftWalkable;
    output.U = isTopWalkable;
  }
}
